import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SearchScope } from "../../constants/Enums";
import { ProfileSearchResult } from "../../interfaces/UserObject";
import { BusinessProfile } from "../../interfaces/BusinessObject";
import { BusinessCollection } from "../../interfaces/BusinessCollectionObject";
import { ListingCollection } from "../../interfaces/ListingCollectionObject";
import { listingCollectionSearchByName } from "../../services/thunks/ListingCollectionThunk";
import { resetAllState } from "./resetAllState";

/**
 * Per-scope search state. Kept in Redux (not SearchPage's local state) so the
 * active scope, query and results survive SearchPage unmounting — e.g. when the
 * user opens a result's profile and presses back. `hasSearched` distinguishes
 * "not searched yet" (render nothing) from "searched, no matches" (empty state).
 *
 * Listings results stay in the productList slice; only the People and Business
 * scopes need their own buckets here.
 */
interface ScopeBucket<T> {
  query: string;
  results: T[];
  hasSearched: boolean;
}

/**
 * The Collections bucket holds collection cards from a name search. Opening one
 * navigates to /collection/<uid> (its own shareable page), so no drill-in state
 * is kept here.
 */
interface SearchState {
  activeScope: SearchScope;
  people: ScopeBucket<ProfileSearchResult>;
  business: ScopeBucket<BusinessProfile>;
  collections: ScopeBucket<BusinessCollection>;
  listingCollections: ScopeBucket<ListingCollection>;
}

const initialState: SearchState = {
  activeScope: SearchScope.LISTINGS,
  people: { query: "", results: [], hasSearched: false },
  business: { query: "", results: [], hasSearched: false },
  collections: { query: "", results: [], hasSearched: false },
  listingCollections: { query: "", results: [], hasSearched: false },
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    setActiveScope(state, action: PayloadAction<SearchScope>) {
      state.activeScope = action.payload;
    },
    setPeopleSearchResults(
      state,
      action: PayloadAction<{ query: string; results: ProfileSearchResult[] }>
    ) {
      state.people = {
        query: action.payload.query,
        results: action.payload.results,
        hasSearched: true,
      };
    },
    setBusinessSearchResults(
      state,
      action: PayloadAction<{ query: string; results: BusinessProfile[] }>
    ) {
      state.business = {
        query: action.payload.query,
        results: action.payload.results,
        hasSearched: true,
      };
    },
    setCollectionSearchResults(
      state,
      action: PayloadAction<{ query: string; results: BusinessCollection[] }>
    ) {
      state.collections = {
        query: action.payload.query,
        results: action.payload.results,
        hasSearched: true,
      };
    },
    setListingCollectionSearchResults(
      state,
      action: PayloadAction<{ query: string; results: ListingCollection[] }>
    ) {
      state.listingCollections = {
        query: action.payload.query,
        results: action.payload.results,
        hasSearched: true,
      };
    },
  },
  extraReducers: (builder) => {
    // Auto-populate listingCollections bucket when the thunk resolves, so
    // results persist in Redux regardless of which component dispatched it.
    builder.addCase(
      listingCollectionSearchByName.fulfilled,
      (state, action) => {
        state.listingCollections = {
          query: "",
          results: action.payload,
          hasSearched: true,
        };
      }
    );
    builder.addCase(resetAllState, () => initialState);
  },
});

export const {
  setActiveScope,
  setPeopleSearchResults,
  setBusinessSearchResults,
  setCollectionSearchResults,
  setListingCollectionSearchResults,
} = searchSlice.actions;

export default searchSlice.reducer;
